import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Wallet, Star, CheckCircle2, Clock, Flame, Lock, Share2, Coins } from "lucide-react";
import api, { TIER_META, AVATARS, flairKey, MILESTONES, avatarUnlock } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RoasterAvatar from "../components/RoasterAvatar";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function Earnings() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [roasts, setRoasts] = useState([]);
  const [avatar, setAvatar] = useState(user?.avatar || null);
  const [milestone, setMilestone] = useState(null);
  const [shimmer, setShimmer] = useState([]);
  const { share, busyId, shareRef, shareItem } = useRoastShare();
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const celebrated = useRef(false);

  const loadEarnings = () => api.get("/roaster/earnings").then((r) => {
    setData(r.data);
    const wins = r.data.accepted_count;
    const key = flairKey(wins);
    if (key && !(r.data.milestones_seen || []).includes(key)) setMilestone(key);
    if (celebrated.current) return;
    const seen = r.data.avatar_unlocks_seen || [];
    const newly = [3, 10, 25].filter((t) => wins >= t && !seen.includes(t));
    if (newly.length) {
      celebrated.current = true;
      const ids = AVATARS.filter((a) => newly.includes(a.unlock)).map((a) => a.id);
      setShimmer(ids);
      toast.success(`New look${ids.length > 1 ? "s" : ""} unlocked — ${ids.map(cap).join(" & ")}! Equip it on your card.`);
      api.post("/profile/unlock-seen", { thresholds: newly }).catch(() => {});
      setTimeout(() => setShimmer([]), 4000);
    }
  });

  useEffect(() => {
    loadEarnings();
    api.get("/roasts/mine").then((r) => setRoasts(r.data));
  }, []);

  useEffect(() => { setAvatar(user?.avatar || null); }, [user]);

  const chooseAvatar = async (id) => {
    if (avatarUnlock(id) > (data?.accepted_count || 0)) {
      toast.error(`${avatarUnlock(id)} wins to unlock this avatar`);
      return;
    }
    const prev = avatar;
    const next = avatar === id ? null : id;
    setAvatar(next);
    try { await api.put("/profile/avatar", { avatar: next }); await checkAuth(); toast.success("Avatar updated — it'll show on your roasts."); }
    catch (e) { setAvatar(prev); toast.error(e?.response?.data?.detail || "Couldn't update avatar"); }
  };

  const shareRank = (key) => {
    share({
      kind: "milestone", flairKey: key, wins: data.accepted_count,
      roaster_name: user?.name, roaster_avatar: avatar,
    }, "rank");
  };

  const dismissMilestone = async () => {
    const key = milestone;
    setMilestone(null);
    try { await api.post("/profile/milestone-seen", { key }); loadEarnings(); }
    catch (e) { toast.error("Couldn't save that — it may show again."); }
  };

  if (!data) return <div className="text-zinc-500 py-16 text-center">Loading…</div>;

  const initials = (user?.name || "R").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const stats = [
    { label: "Pending payout", value: `${data.settlement_currency} ${data.pending_settlement.toFixed(2)}`, icon: Clock, color: "#f59e0b" },
    { label: "Accepted roasts", value: data.accepted_count, icon: CheckCircle2, color: "#10b981" },
    { label: "Avg rating", value: data.rating_count ? `${data.average_rating} ★` : "—", icon: Star, color: "#ffeb3b" },
    { label: `Est. total (${data.settlement_currency})`, value: `${data.settlement_currency} ${data.total_earned_settlement.toFixed(2)}`, icon: Wallet, color: "#ff4500" },
    { label: "Tips received", value: `₹${(data.total_tips || 0).toFixed(2)}`, icon: Coins, color: "#d4af37" },
  ];

  const ms = milestone ? MILESTONES[milestone] : null;
  const rankKey = flairKey(data.accepted_count);

  // rank progress + next avatar unlock nudge
  const wins = data.accepted_count;
  const RANK_ORDER = ["rising", "top", "legend"];
  const currentRankKey = [...RANK_ORDER].reverse().find((k) => wins >= MILESTONES[k].threshold) || null;
  const floor = currentRankKey ? MILESTONES[currentRankKey].threshold : 0;
  const nextRankKey = RANK_ORDER.find((k) => MILESTONES[k].threshold > wins) || null;
  const nextThreshold = nextRankKey ? MILESTONES[nextRankKey].threshold : null;
  const rankPct = nextThreshold ? Math.min(100, Math.round(((wins - floor) / (nextThreshold - floor)) * 100)) : 100;
  const winsToNextRank = nextThreshold ? nextThreshold - wins : 0;
  const nextLocked = AVATARS.find((a) => a.unlock > wins);
  const winsToUnlock = nextLocked ? nextLocked.unlock - wins : 0;

  return (
    <div>
      <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Earnings</h1>
      <p className="text-zinc-400 mt-2 mb-8">Your roasting scoreboard and payout history.</p>

      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <RoasterAvatar avatar={avatar} initials={initials} size={44} ring="#ff4500" />
            <div>
              <div className="text-sm font-semibold text-zinc-100">Your roaster avatar</div>
              <div className="text-xs text-zinc-500">Unlock new looks as you rack up wins. No real photo, ever.</div>
            </div>
          </div>
          {rankKey && (
            <button data-testid="share-rank-btn" onClick={() => shareRank(rankKey)} disabled={busyId === "rank"}
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold text-sm px-4 py-2 transition-colors">
              <Share2 size={15} /> {busyId === "rank" ? "Preparing…" : "Share my rank"}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {AVATARS.map((a) => {
            const locked = avatarUnlock(a.id) > data.accepted_count;
            return (
              <button key={a.id} data-testid={`avatar-${a.id}`} onClick={() => chooseAvatar(a.id)} disabled={locked}
                title={locked ? `${avatarUnlock(a.id)} wins to unlock` : ""}
                className={`relative transition-transform rounded-full ${locked ? "cursor-not-allowed" : "hover:scale-105"} ${avatar === a.id ? "ring-2 ring-offset-2 ring-offset-zinc-900 ring-brand" : ""} ${shimmer.includes(a.id) ? "animate-unlock" : ""}`}>
                <div className={locked ? "opacity-30 grayscale" : ""}>
                  <RoasterAvatar avatar={a.id} size={44} ring={avatar === a.id ? "#ff4500" : "rgba(255,255,255,0.25)"} />
                </div>
                {locked && (
                  <span data-testid={`avatar-lock-${a.id}`} className="absolute inset-0 flex flex-col items-center justify-center rounded-full">
                    <Lock size={14} className="text-white" />
                    <span className="text-[9px] font-bold text-white leading-none mt-0.5">{avatarUnlock(a.id)}</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-zinc-400 font-semibold uppercase tracking-widest">
              {currentRankKey ? MILESTONES[currentRankKey].label : "Starter"}
            </span>
            {nextRankKey ? (
              <span className="text-zinc-500" data-testid="rank-progress-label">
                <span className="text-brand font-bold">{winsToNextRank}</span> win{winsToNextRank === 1 ? "" : "s"} to{" "}
                <span className="text-zinc-200 font-bold uppercase tracking-wide">{MILESTONES[nextRankKey].label}</span>
              </span>
            ) : (
              <span className="text-brand font-bold uppercase tracking-widest" data-testid="rank-progress-label">Max rank — Legend</span>
            )}
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden" data-testid="rank-progress-bar">
            <div className="h-full bg-brand rounded-full transition-[width] duration-700" style={{ width: `${nextRankKey ? Math.max(6, rankPct) : 100}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            {nextLocked ? (
              <div className="flex items-center gap-1.5 text-xs text-zinc-400" data-testid="unlock-nudge">
                <Lock size={12} className="text-zinc-500 shrink-0" />
                <span>
                  <span className="text-brand font-bold">{winsToUnlock}</span> more win{winsToUnlock === 1 ? "" : "s"} to unlock the{" "}
                  <span className="text-zinc-100 font-semibold">{cap(nextLocked.id)}</span> avatar
                </span>
              </div>
            ) : <span />}
            {nextRankKey && (
              <button data-testid="share-progress-btn" disabled={busyId === "progress"}
                onClick={() => share({
                  kind: "progress",
                  rankLabel: currentRankKey ? MILESTONES[currentRankKey].label : null,
                  nextLabel: MILESTONES[nextRankKey].label, nextKey: nextRankKey,
                  wins, nextThreshold, floor, pct: rankPct,
                  roaster_name: user?.name, roaster_avatar: avatar,
                }, "progress")}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-brand/40 text-brand hover:bg-brand/10 disabled:opacity-60 text-xs font-bold px-3 py-1.5 transition-colors">
                <Share2 size={13} /> {busyId === "progress" ? "Preparing…" : "Share progress"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => (
          <div key={s.label} data-testid={`stat-${s.label}`} className="rounded-sm border border-white/10 bg-zinc-900/60 p-5">
            <s.icon size={18} style={{ color: s.color }} />
            <div className="font-heading text-3xl font-bold mt-3">{s.value}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <Dialog open={!!milestone} onOpenChange={(o) => { if (!o) dismissMilestone(); }}>
        <DialogContent className="bg-zinc-950 border-brand/40 max-w-sm text-center" data-testid="milestone-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-3xl flex items-center justify-center gap-2">
              <Flame className="text-brand" size={26} /> {ms?.label}
            </DialogTitle>
            <DialogDescription className="text-zinc-300 text-base">{ms?.blurb}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <span className="text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full bg-brand text-black">{ms?.label} unlocked</span>
          </div>
          <div className="flex flex-col gap-2">
            <button data-testid="milestone-share" onClick={() => shareRank(milestone)} disabled={busyId === "rank"}
              className="w-full py-3 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold transition-colors inline-flex items-center justify-center gap-2">
              <Share2 size={16} /> {busyId === "rank" ? "Preparing…" : "Share to story 🔥"}
            </button>
            <button data-testid="milestone-claim" onClick={dismissMilestone}
              className="w-full py-2.5 rounded-full border border-white/15 hover:bg-white/5 text-zinc-200 font-semibold transition-colors">
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ShareCardPortal shareRef={shareRef} item={shareItem} />

      {data.by_currency.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-10">
          {data.by_currency.map((b) => (
            <span key={b.currency} data-testid={`earn-cur-${b.currency}`} className="text-xs rounded-full border border-white/10 bg-zinc-900/60 px-3 py-1.5">
              <span className="text-zinc-500">{b.currency}</span> <span className="font-bold text-tier-mild">{b.pending.toFixed(2)} pending</span> · {b.paid.toFixed(2)} paid
            </span>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">Payout history</h2>
          {data.payout_history.length === 0 ? <p className="text-zinc-500 text-sm">No payouts yet.</p> : (
            <div className="space-y-2">
              {data.payout_history.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-sm border border-white/10 bg-zinc-900/40 px-4 py-3">
                  <span className="text-sm text-zinc-400">{p.kind === "attempt_credit" ? "Attempt credit" : "Accepted roast"}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold text-tier-mild">{p.currency || ""} {p.amount.toFixed(2)}</span>
                    <span className={`text-[11px] uppercase tracking-widest ${p.status === "paid" ? "text-tier-mild" : "text-brand-accent"}`}>{p.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">Your roasts</h2>
          {roasts.length === 0 ? <p className="text-zinc-500 text-sm">You haven't roasted anyone yet.</p> : (
            <div className="space-y-2">
              {roasts.map((r) => (
                <div key={r.id} className="rounded-sm border border-white/10 bg-zinc-900/40 p-4">
                  <p className="text-sm text-zinc-200 line-clamp-2">"{r.content_text}"</p>
                  <div className="flex items-center gap-2 mt-2">
                    {r.submission && <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: TIER_META[r.submission.tier].color }}>{TIER_META[r.submission.tier].label}</span>}
                    <span className={`text-[11px] uppercase tracking-widest ${r.status === "selected" ? "text-tier-mild" : r.status === "rejected_by_mod" ? "text-destructive" : "text-zinc-500"}`}>{r.status.replace(/_/g, " ")}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
